/* ─────────────────────────────────────────────────────────────────────────
   The vocabulary of a status page — one copy, shared by the Worker and the
   browser.

   These names appear in the database's CHECK constraints, in the probe that
   writes them, in the API that publishes them and in the CSS that colours
   them. Four copies of "which strings are a valid state" drift, and the drift
   is silent: a monitor lands in a state the page has no colour for and renders
   as nothing at all.

   Deliberately dependency-free, like website-cloud's shared/entitlements.ts,
   so both sides can import it without dragging a runtime along.
   ───────────────────────────────────────────────────────────────────────── */

// ── monitor state ───────────────────────────────────────────────────────────

/**
 * What a monitor is doing right now.
 *
 * `unknown` is the state a monitor is born in and the one it returns to when
 * its history is wiped — never a synonym for down. A monitor nobody has
 * probed yet has not failed, and saying so is the difference between an honest
 * page and one that cries outage on its first deploy.
 */
export const MONITOR_STATES = ['up', 'down', 'maintenance', 'paused', 'unknown'] as const;
export type MonitorState = (typeof MONITOR_STATES)[number];

/** The three a *check* can be classified as. A check is never paused (a paused
 *  monitor is not probed) and never unknown (it just happened). */
export const CHECK_STATES = ['up', 'down', 'maintenance'] as const;
export type CheckState = (typeof CHECK_STATES)[number];

export const MONITOR_KINDS = ['http', 'keyword', 'json', 'tcp'] as const;
export type MonitorKind = (typeof MONITOR_KINDS)[number];

// ── incidents ───────────────────────────────────────────────────────────────

export const INCIDENT_KINDS = ['incident', 'maintenance'] as const;
export type IncidentKind = (typeof INCIDENT_KINDS)[number];

export const IMPACTS = ['none', 'minor', 'major', 'critical'] as const;
export type Impact = (typeof IMPACTS)[number];

/**
 * The two lifecycles, kept apart on purpose.
 *
 * An incident is discovered and then understood; a maintenance window is
 * announced before it happens. Collapsing them into one list would put
 * "investigating" on a window somebody scheduled a fortnight ago.
 */
export const INCIDENT_STATES = ['investigating', 'identified', 'monitoring', 'resolved'] as const;
export const MAINTENANCE_STATES = ['scheduled', 'in_progress', 'completed'] as const;
export type IncidentState = (typeof INCIDENT_STATES)[number];
export type MaintenanceState = (typeof MAINTENANCE_STATES)[number];
export type AnyIncidentState = IncidentState | MaintenanceState;

export const statesFor = (kind: IncidentKind): readonly AnyIncidentState[] =>
  kind === 'maintenance' ? MAINTENANCE_STATES : INCIDENT_STATES;

/** Terminal states — the ones that take an incident off the current board and
 *  into history. */
export const isClosed = (state: string): boolean => state === 'resolved' || state === 'completed';

// ── the page's overall verdict ──────────────────────────────────────────────

/**
 * The sentence at the top of the page, worst-first.
 *
 * Ordered by severity so `worstOf` can take a max, and phrased the way a status
 * page phrases it — a visitor reads this line and nothing else, so it has to
 * carry the whole answer on its own.
 */
export const OVERALL = [
  'operational',
  'maintenance',
  'degraded',
  'partial_outage',
  'major_outage'
] as const;
export type Overall = (typeof OVERALL)[number];

export const OVERALL_LABEL: Record<Overall, string> = {
  operational: 'All Systems Operational',
  maintenance: 'Under Maintenance',
  degraded: 'Degraded Performance',
  partial_outage: 'Partial System Outage',
  major_outage: 'Major System Outage'
};

/**
 * The page's verdict, from the states of the monitors on it.
 *
 * Maintenance ranks *below* any real fault: a window open on one service while
 * another is genuinely down must not soften the headline to "Under
 * Maintenance". It ranks above operational because a visitor whose request is
 * failing deserves to see that we know why.
 *
 * `paused` and `unknown` contribute nothing. Neither is a claim about whether
 * the service works, and counting them either way would be inventing one.
 */
export function overallFrom(states: readonly MonitorState[]): Overall {
  const live = states.filter((s) => s !== 'paused' && s !== 'unknown');
  if (!live.length) return 'operational';

  const down = live.filter((s) => s === 'down').length;
  if (down >= live.length && down > 0) return 'major_outage';
  // More than half the board is a major outage even if something still answers.
  if (down > live.length / 2) return 'major_outage';
  if (down > 0) return 'partial_outage';
  if (live.some((s) => s === 'maintenance')) return 'maintenance';
  return 'operational';
}

// ── uptime ──────────────────────────────────────────────────────────────────

/**
 * One day of a monitor's history, as the rollup table holds it.
 *
 * Seconds rather than check counts, because a monitor whose interval changed
 * partway through a day would otherwise weight its two halves differently. Each
 * check contributes its own `interval_seconds` to exactly one of these three.
 */
export interface DayTotals {
  upSeconds: number;
  downSeconds: number;
  maintenanceSeconds: number;
}

/**
 * **The rule the whole product turns on: planned maintenance is excluded.**
 *
 * Not counted as up — excluded from numerator *and* denominator. A window is a
 * period nobody was measuring, so it neither helps nor hurts the figure.
 *
 * Returns `null`, not 0 and not 100, when there is nothing to divide by. A day
 * spent entirely in a maintenance window, or a monitor created this morning,
 * has no uptime to report, and every caller has to render that as "no data"
 * rather than pick a number. Returning 100 would be the flattering lie and
 * returning 0 the alarming one; both would be made up.
 */
export function uptimePercent(totals: DayTotals): number | null {
  const measured = totals.upSeconds + totals.downSeconds;
  if (measured <= 0) return null;
  return (totals.upSeconds / measured) * 100;
}

/** Sum a run of days, then take the percentage — never the mean of the daily
 *  percentages, which would weight a quiet day the same as a full one. */
export function sumDays(days: readonly DayTotals[]): DayTotals {
  return days.reduce<DayTotals>(
    (acc, d) => ({
      upSeconds: acc.upSeconds + d.upSeconds,
      downSeconds: acc.downSeconds + d.downSeconds,
      maintenanceSeconds: acc.maintenanceSeconds + d.maintenanceSeconds
    }),
    { upSeconds: 0, downSeconds: 0, maintenanceSeconds: 0 }
  );
}

/**
 * How a percentage is written on the page.
 *
 * Two decimals below 100 and none at it, which is the convention every status
 * page follows: "99.98%" is information, and "100.00%" is noise. Rounds *down*
 * at the top so a monitor with four seconds of downtime this quarter reads
 * 99.99% rather than being rounded up into a claim of perfection.
 */
export function formatUptime(percent: number | null): string {
  if (percent === null) return '—';
  if (percent >= 100) return '100%';
  const floored = Math.floor(percent * 100) / 100;
  return `${floored.toFixed(2)}%`;
}

// ── days ────────────────────────────────────────────────────────────────────

/** The `YYYY-MM-DD` key the rollup table buckets by. UTC, always: a local-time
 *  key would silently re-bucket every historical row the day the server's
 *  offset changed. */
export function dayKey(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toISOString().slice(0, 10);
}

/** `count` day keys ending today, oldest first — the x-axis of the bar strip. */
export function recentDays(count: number, nowSeconds: number): string[] {
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i--) out.push(dayKey(nowSeconds - i * 86_400));
  return out;
}
